import { useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import Sidebar from '@/components/layout/Sidebar';
import Header from '@/components/layout/Header';
import AIChatButton from '@/components/chat/AIChatButton';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePresence } from '@/hooks/usePresence';
import { useTenant } from '@/hooks/useTenant';

interface AppShellProps {
  activeView: string;
  children: ReactNode;
}

// Wraps a standalone route (Integrations, Settings, Signals — pages that
// live outside Index.tsx's in-page view switch) with the same Sidebar +
// Header chrome the main app shell uses, so navigating to one of these
// pages doesn't strand the user without app-wide navigation.
const AppShell = ({ activeView, children }: AppShellProps) => {
  const navigate = useNavigate();
  const { tenantSlug } = useTenant();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const { onlineUsers } = usePresence(activeView);

  // Sidebar items other than Integrations/Settings map to views inside
  // Index.tsx's renderContent() switch, not standalone routes — selecting
  // one from here has to navigate back into the app shell with the view
  // pre-selected via the `?view=` deep-link Index.tsx reads on mount.
  const handleViewChange = (view: string) => {
    navigate(tenantSlug ? `/${tenantSlug}/app?view=${view}` : '/');
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        activeView={activeView}
        onViewChange={handleViewChange}
        isOpen={sidebarOpen}
        onOpenChange={setSidebarOpen}
      />

      <div className={cn(
        'h-screen flex flex-col transition-all duration-300',
        !isMobile && 'ms-72'
      )}>
        <Header
          isDark={isDark}
          onThemeToggle={() => setTheme(isDark ? 'light' : 'dark')}
          onMenuClick={() => setSidebarOpen(true)}
          onlineUsers={onlineUsers}
        />

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </main>
      </div>

      <AIChatButton />
    </div>
  );
};

export default AppShell;
